/**
 * New Year icons — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearIconsPack: ToilePack = {
    slug: 'new-year-icons',
    category: 'season',
    name: 'New Year icons',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'glass', label: 'Glass icon', subject: 'A champagne flute reduced to one simple glyph, built to read small, isolated on a plain ground'},
        {key: 'firework', label: 'Firework icon', subject: 'A firework burst as one simple glyph, built to read small, isolated on a plain ground'},
        {key: 'clock', label: 'Clock icon', subject: 'A clock face at twelve as one simple glyph, built to read small, isolated on a plain ground'},
    ],
    styles: [
        {key: 'deco', label: 'Art deco', style: 'art deco illustration, geometric fans and sunbursts, clean lines, no outlines', palette: 'black and gold with a little cream', framing: 'Centered on a solid black background with an even margin'},
        {key: 'neon', label: 'Neon party', style: 'neon party graphic, glowing lines, confetti and streamers', palette: 'electric pink, cyan and gold on deep navy', framing: 'Centered on a dark background, built to read clean at small size'},
        {key: 'minimal', label: 'Minimal', style: 'modern minimal illustration, few flat shapes, generous space', palette: 'gold and white with a single black', framing: 'Centered on a plain white background with generous space around it'},
    ],
    directions: ['a confetti icon instead', 'as a filled shape', 'as a matching icon set'],
    directionHint: 'try: a confetti icon instead',
    seo: {
        title: 'New Year Icon Set: Champagne, Firework, Clock',
        metaDescription: 'Nine new year icons — champagne glass, firework, clock — built as clean glyphs that read at small sizes, in three styles.',
        h1: 'New year icon set: champagne, firework, clock',
        tagline: 'Nine glyphs, three midnight subjects, three styles each.',
        intro: 'A small, clean icon set for midnight: a champagne glass, a firework, a clock, each drawn as a simple glyph that stays legible even shrunk down. Ask for a party hat, a confetti burst, or your own subject, and the same three styles carry it.',
        useCases: [
            "a website or app New Year's banner",
            'a slide deck or presentation graphic',
            'a printed program or menu icon',
            'a social media highlight cover',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine icons — a champagne glass, a firework, a clock — drawn as simple glyphs, in three styles.'},
            {q: 'Will these still look sharp at a small size?', a: 'Yes — each glyph is built with clean shapes and thick lines so it stays readable at icon size, not just at full resolution.'},
            {q: 'Can I get a different icon, like a party hat?', a: 'Yes — the direction field takes any new year subject, including a party hat, confetti, or a calendar page.'},
            {q: 'Do I get a vector file?', a: 'No — each icon renders as a flat image, not an editable vector; it works well as a small PNG for web or print.'},
            {q: "Can I use these in a product or app I'm building?", a: 'Yes — check the license terms on your account for the specifics of commercial use.'},
        ],
    },
}
